import React, { useRef, useState } from 'react'
import moment from 'moment'
import { find, findIndex, path, pathEq, prop, propEq } from 'ramda'
import { Menu } from 'primereact/menu'
import { ProgressSpinner } from 'primereact/progressspinner'
import { TAppointmentsItem, TGetDataFromState, TUseCreateModal, TUseDelete, TUseUpdateModal } from 'types'
import { getFullName } from 'utils/get'
import * as STATE from '../../../constants/states'
import { useTypedSelector } from '../../../etc/reducers'
import { getDataFromState } from '../../../utils/get'
import { COLORS } from '../../../constants/constants'

import Button from '../../../components/Button/Button'
import { useCalendarContext } from '../calendar-provider'
import CalendarCreateDrawer from './CalendarCreateDrawer'
import {
  ColumnViewWrapper,
  NavigationBar,
  GridScrollContainer,
  GridTable,
  DateHeaderCell,
  DoctorHeaderCell,
  DoctorAvatar,
  DoctorAvatarPlaceholder,
  DoctorName,
  TimeCell,
  BodyCell,
  AppointmentCard,
  CardTime,
  CardTitle,
  EmptyHeaderCell,
} from './ColumnView.styled'
import useCalendarActions from './useCalendarActions'

const SOFT_COLORS = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899',
  '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
  '#e11d48', '#7c3aed', '#0891b2', '#059669', '#d946ef',
]
interface Props {
  createData: TUseCreateModal<TAppointmentsItem>
  listData: TGetDataFromState<any>
  deleteData: TUseDelete
  confirmData: TUseDelete
  updateData: TUseUpdateModal<any>
  gotDetail: (id) => void
  doctor?: string | undefined
  isDirector?: boolean
  personListData?: TGetDataFromState<any>
}

const START_HOUR = 8
const END_HOUR = 20
const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i)

const getMinuteOffset = (timeStr: string) => {
  const [h, m] = timeStr.split(':').map(Number)
  return m
}

const getMinuteDuration = (startTime: string, endTime: string) => {
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  return (eh * 60 + em) - (sh * 60 + sm)
}

const ColumnView = (props: Props) => {
  const {
    doctor,
    isDirector,
    personListData,
    gotDetail,
    createData,
    listData,
    deleteData,
    confirmData,
    updateData,
  } = props

  const doctorDataList = personListData?.data?.data?.filter((obj) => find(propEq('doctor', 'name'), obj?.roles)) || []
  const profile = useTypedSelector(state => getDataFromState(STATE.PROFILE, state))
  const profileId = path(['data', 'data', '_id'], profile)
  const menu = useRef(null)
  const { commit, state } = useCalendarContext()
  const calendarRef = useRef(null)

  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek'))
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const todayColumnRef = useRef<HTMLDivElement>(null)
  const visibleDates = Array.from({ length: 7 }, (_, i) => moment(weekStart).add(i, 'days'))
  const todayStr = moment().format('YYYY-MM-DD')

  const scrollToToday = () => {
    setTimeout(() => {
      if (todayColumnRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current
        const todayEl = todayColumnRef.current
        const scrollLeft = todayEl.offsetLeft - container.offsetLeft - 60
        container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' })
      }
    }, 50)
  }

  const { handleEventDelete } = useCalendarActions({
    state,
    calendarRef,
    commit,
  })

  const allAppointments: any[] = path(['data', 'data'], listData) || []
  const filtered = allAppointments
    .filter((obj) => (isDirector ? true : pathEq(profileId, ['doctor', '_id'], obj)))

  // Barcha doctorlarni bir xil tartibda har kuni ko'rsatish
  const allDoctors = doctorDataList.map((doc) => ({
    _id: doc._id as string,
    name: getFullName(doc),
    avatar: doc.avatar || '',
  }))

  type DayColumn = { date: moment.Moment; dateStr: string; doctors: { _id: string; name: string; avatar?: string }[] }
  const dayColumns: DayColumn[] = visibleDates.map((date) => {
    const dateStr = date.format('YYYY-MM-DD')
    return {
      date,
      dateStr,
      doctors: allDoctors.length > 0 ? allDoctors : [{ _id: '', name: '—' }],
    }
  })

  const totalColumns = dayColumns.reduce((sum, d) => sum + d.doctors.length, 0)

  // Appointment ni topish: kun + doctor
  const getAppointmentsFor = (dateStr: string, doctorId: string) => {
    return filtered.filter((a) => a.date === dateStr && a.doctorId === doctorId)
  }

  const handleCellClick = (date: moment.Moment, hour: number, doctorId: string) => {
    if (!doctorId) return
    createData.onOpen()
    commit({
      selectDateEvents: [],
      initialValues: {
        date: date.toDate(),
        startTime: `${String(hour).padStart(2, '0')}:00`,
        endTime: `${String(hour + 1).padStart(2, '0')}:00`,
        doctorId: doctorId || undefined,
      },
    })
  }

  const handleAppointmentClick = (e: React.MouseEvent, appointment: any) => {
    e.stopPropagation()
    menu.current.show(e.nativeEvent)
    setTimeout(() => {
      commit({
        OPStyles: { top: `${e.pageY}px`, left: `${e.pageX}px` },
        activeEvent: appointment._id,
        activeEventData: appointment,
      })
    }, 0)
  }

  const items = [
    {
      label: 'Опции',
      items: [
        {
          label: 'Просмотреть',
          icon: 'pi pi-arrow-up-right',
          command: () => gotDetail(state.activeEvent),
        },
        {
          label: 'Изменить запись',
          icon: 'pi pi-pencil',
          command: () => {
            const appt = state.activeEventData
            commit({
              selectDateEvents: [],
              initialValues: {
                date: moment(appt.date).toDate(),
                note: appt.note,
                startTime: appt.startTime?.slice(0, 5),
                endTime: appt.endTime?.slice(0, 5),
                doctorId: appt.doctorId,
                clinicId: appt.clinicId,
                patientId: appt.patientId,
              },
            })
            updateData.onOpen(state.activeEvent)
          },
        },
        state.activeEventData?.status === 'CREATED_BY_PATIENT' && {
          label: 'Подтвердить запись',
          icon: 'pi pi-check',
          command: () => confirmData.onSubmit(state.activeEvent),
        },
        {
          label: 'Удалить запись',
          icon: 'pi pi-times',
          command: () => handleEventDelete(state.activeEvent),
        },
      ].filter(Boolean),
    },
  ]

  if (listData.loading || personListData.loading) {
    return (
      <div className="flex justify-content-center h-30rem align-items-center">
        <ProgressSpinner />
      </div>
    )
  }

  return (
    <>
      <ColumnViewWrapper>
        <NavigationBar>
          <Button
            icon="pi pi-chevron-left"
            className="p-button-text p-button-sm"
            onClick={() => setWeekStart((prev) => moment(prev).subtract(7, 'days'))}
          />
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {weekStart.format('DD MMM')} — {moment(weekStart).add(6, 'days').format('DD MMM YYYY')}
          </span>
          <Button
            icon="pi pi-chevron-right"
            className="p-button-text p-button-sm"
            onClick={() => setWeekStart((prev) => moment(prev).add(7, 'days'))}
          />
          <Button
            label="Сегодня"
            className="p-button-text p-button-sm"
            onClick={() => {
              setWeekStart(moment().startOf('isoWeek'))
              scrollToToday()
            }}
          />
        </NavigationBar>

        <GridScrollContainer ref={scrollContainerRef}>
          <GridTable columns={totalColumns} rows={hours.length}>
            {/* === Date header row === */}
            <EmptyHeaderCell />
            {dayColumns.map((day) => {
              const isToday = day.dateStr === todayStr
              return (
                <DateHeaderCell
                  key={day.dateStr}
                  span={day.doctors.length}
                  isToday={isToday}
                  ref={isToday ? todayColumnRef : undefined}
                >
                  {day.date.format('DD MMM, dd')}
                </DateHeaderCell>
              )
            })}

            {/* === Doctor header row === */}
            <EmptyHeaderCell style={{ borderBottom: '1px solid #dee2e6' }} />
            {dayColumns.map((day) =>
              day.doctors.map((doc, docIdx) => (
                <DoctorHeaderCell
                  key={`${day.dateStr}-${doc._id}`}
                  bgColor={doc._id ? SOFT_COLORS[findIndex(propEq(doc._id, '_id'), doctorDataList) % SOFT_COLORS.length] : '#94a3b8'}
                  isLastInDay={docIdx === day.doctors.length - 1}
                >
                  {doc.avatar ? (
                    <DoctorAvatar src={doc.avatar.startsWith('data:') ? doc.avatar : `data:image/png;base64,${doc.avatar}`} alt={doc.name} />
                  ) : (
                    <DoctorAvatarPlaceholder>
                      <i className="pi pi-user" />
                    </DoctorAvatarPlaceholder>
                  )}
                  <DoctorName>{doc.name}</DoctorName>
                </DoctorHeaderCell>
              ))
            )}

            {/* === Body: hours x columns === */}
            {hours.map((hour) => (
              <React.Fragment key={hour}>
                <TimeCell>{`${String(hour).padStart(2, '0')}:00`}</TimeCell>
                {dayColumns.map((day) =>
                  day.doctors.map((doc, docIdx) => {
                    const appointments = doc._id ? getAppointmentsFor(day.dateStr, doc._id) : []
                    const hourAppointments = appointments.filter((a) => {
                      const h = parseInt(a.startTime?.split(':')[0], 10)
                      return h === hour
                    })
                    const isLast = docIdx === day.doctors.length - 1
                    const docColor = doc._id ? SOFT_COLORS[findIndex(propEq(doc._id, '_id'), doctorDataList) % SOFT_COLORS.length] : undefined
                    return (
                      <BodyCell
                        key={`${day.dateStr}-${doc._id}-${hour}`}
                        isLastInDay={isLast}
                        isToday={day.dateStr === todayStr}
                        borderColor={!isLast ? docColor : undefined}
                        onClick={() => handleCellClick(day.date, hour, doc._id)}
                      >
                        {hourAppointments.map((appt) => {
                          const topPercent = (getMinuteOffset(appt.startTime) / 60) * 100
                          const durationMinutes = getMinuteDuration(appt.startTime, appt.endTime)
                          const heightPercent = (durationMinutes / 60) * 100
                          return (
                            <AppointmentCard
                              key={appt._id}
                              top={`${topPercent}%`}
                              height={`${heightPercent}%`}
                              bgColor={SOFT_COLORS[findIndex(propEq(appt.doctorId, '_id'), doctorDataList) % SOFT_COLORS.length]}
                              onClick={(e) => handleAppointmentClick(e, appt)}
                            >
                              <CardTime>{appt.startTime?.slice(0, 5)} - {appt.endTime?.slice(0, 5)}</CardTime>
                              <CardTitle>
                                {appt.note} - {getFullName(appt.patient)}
                              </CardTitle>
                            </AppointmentCard>
                          )
                        })}
                      </BodyCell>
                    )
                  })
                )}
              </React.Fragment>
            ))}
          </GridTable>
        </GridScrollContainer>
      </ColumnViewWrapper>

      <Menu model={items} popup ref={menu} style={{ ...state.OPStyles }} />
      <CalendarCreateDrawer
        selectDateEvents={state.selectDateEvents || []}
        state={state}
        commit={commit}
        ref={calendarRef}
        open={createData.open}
        loading={createData.loading}
        onClose={createData.onClose}
        onSubmit={createData.onSubmit}
      />
      <CalendarCreateDrawer
        selectDateEvents={state.selectDateEvents || []}
        state={state}
        commit={commit}
        ref={calendarRef}
        onSubmit={updateData.onSubmit}
        open={updateData.open}
        isUpdate={true}
        loading={updateData.loading}
        onClose={updateData.onClose}
      />
    </>
  )
}

export default ColumnView
